import React, { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';

import type {
  ICartImportPreviewInvalidRow,
  ICartImportPreviewSummary,
  ICartImportPreviewValidRow,
} from 'api/endpoints/usePostCartImportPreview';

import styles from 'theme/components/wise/WiseCartImportModal/WiseCartImportModal.module.scss';

export type CartImportPreviewSummaryProps = {
  valid: ICartImportPreviewValidRow[];
  invalid: ICartImportPreviewInvalidRow[];
  summary: ICartImportPreviewSummary;
};

const resolveProductName = (
  name: ICartImportPreviewValidRow['product_name']
): string | null => {
  if (!name) return null;
  if (typeof name === 'string') return name;
  if (Array.isArray(name) && name.length > 0) {
    return name[0]?.translation ?? null;
  }
  return null;
};

const formatInvalidReason = (
  row: ICartImportPreviewInvalidRow,
  t: (key: string, options?: object) => string
): string => {
  switch (row.reason) {
    case 'product_not_found':
      return t('Nie znaleziono produktu');
    case 'unit_not_available':
      return row.available_units && row.available_units.length > 0
        ? t('Jednostka niedostepna dla produktu (dostepne: {{units}})', {
            units: row.available_units.join(', '),
          })
        : t('Jednostka niedostepna dla produktu');
    case 'unit_missing':
      return t('Brak jednostki');
    case 'invalid_quantity':
      return t('Nieprawidlowa ilosc');
    case 'invalid_row_length':
      return t('Za malo kolumn w wierszu');
    case 'empty_key':
      return t('Pusta kolumna KTM');
    default:
      return row.reason;
  }
};

const CartImportPreviewSummary: FC<CartImportPreviewSummaryProps> = ({
  valid,
  invalid,
  summary,
}) => {
  const { t } = useTranslation();

  const rows = useMemo(
    () =>
      valid.map((row) => ({
        ...row,
        displayName: resolveProductName(row.product_name) ?? row.product_key,
      })),
    [valid]
  );

  return (
    <div className={styles.previewContainer}>
      <div className={styles.summary}>
        <div className={styles.summaryItem}>
          <span>{t('Wierszy razem')}:</span>
          <span className={styles.summaryValue}>{summary.total}</span>
        </div>
        <div className={styles.summaryItem}>
          <span>{t('Dodano X pozycji', { count: summary.will_add })}</span>
        </div>
        <div className={styles.summaryItem}>
          <span>{t('Polaczono X pozycji', { count: summary.will_merge })}</span>
        </div>
        {summary.invalid_count > 0 && (
          <div className={styles.summaryItem}>
            <span>{t('Pominieto X produktow', { count: summary.invalid_count })}</span>
          </div>
        )}
      </div>

      {rows.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t('KTM')}</th>
              <th>{t('Nazwa')}</th>
              <th>{t('Ilosc')}</th>
              <th>{t('Jednostka')}</th>
              <th>{t('Status')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${row.row_number}-${row.product_id}`}>
                <td>{row.product_symbol ?? row.product_key}</td>
                <td>{row.displayName}</td>
                <td>{row.quantity}</td>
                <td>{row.unit ?? '-'}</td>
                <td className={styles.statusCell}>
                  {row.already_in_cart ? (
                    <span
                      className={classnames(styles.badge, styles.badgeMerge)}
                      title={`${row.current_quantity} + ${row.quantity} = ${row.resulting_quantity}`}>
                      <span>{row.current_quantity}</span>
                      <span className={styles.badgeArrow} aria-hidden="true">
                        →
                      </span>
                      <span>{row.resulting_quantity}</span>
                    </span>
                  ) : (
                    <span className={classnames(styles.badge, styles.badgeNew)}>
                      {t('Nowy')}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {invalid.length > 0 && (
        <div className={styles.invalidSection}>
          <div className={styles.invalidTitle}>{t('Niepoprawne wiersze')}</div>
          {invalid.map((row, index) => {
            const label = row.product_key ?? row.raw_value ?? '';
            return (
              <div key={`${row.row_number}-${index}`} className={styles.invalidItem}>
                {t('Wiersz')} {row.row_number}
                {label ? ` (${label})` : ''}: {formatInvalidReason(row, t)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CartImportPreviewSummary;
